/**
 * Admin Referral Payouts Page
 *
 * Displays referral payout overview stats, pending payout requests for approval,
 * and payout history. Allows admins to approve, reject, or retry payouts.
 */

import React, { useEffect, useState } from 'react';
import {
  getAdminReferralPayoutStats,
  getAdminReferralPayouts,
  getAdminReferralPayoutDetail,
  approveReferralPayout,
  rejectReferralPayout,
  retryReferralPayout,
  type AdminReferralPayout,
  type ReferralLiabilityStats,
} from '../services/adminApi.js';
import { AdminLayout } from '../components/AdminLayout.js';

function formatXrpFromDrops(drops: number): string {
  const xrp = drops / 1_000_000;
  return xrp % 1 === 0 ? `${xrp} XRP` : `${parseFloat(xrp.toFixed(6))} XRP`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatAddr(addr: string): string {
  if (!addr || addr.length < 12) return addr || '-';
  return `${addr.slice(0, 8)}...${addr.slice(-4)}`;
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-blue-100 text-blue-800',
    processing: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    rejected: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
      {status === 'completed' ? 'Sent' : status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

export function AdminReferralPayoutsPage() {
  const [stats, setStats] = useState<ReferralLiabilityStats | null>(null);
  const [payouts, setPayouts] = useState<AdminReferralPayout[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [detailData, setDetailData] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchData = async () => {
    try {
      const [statsData, payoutsData] = await Promise.all([
        getAdminReferralPayoutStats(),
        getAdminReferralPayouts({ limit: 50 }),
      ]);
      setStats(statsData);
      setPayouts(payoutsData.payouts);
      setTotal(payoutsData.total);
    } catch (err) {
      console.error('Failed to load referral payout data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleApprove = async (id: string) => {
    setActionLoading(id);
    try { await approveReferralPayout(id); await fetchData(); } catch (err) { console.error(err); } finally { setActionLoading(null); }
  };

  const handleReject = async () => {
    if (!rejectId || !rejectReason.trim()) return;
    setActionLoading(rejectId);
    try { await rejectReferralPayout(rejectId, rejectReason); setRejectId(null); setRejectReason(''); await fetchData(); } catch (err) { console.error(err); } finally { setActionLoading(null); }
  };

  const handleRetry = async (id: string) => {
    setActionLoading(id);
    try { await retryReferralPayout(id); await fetchData(); } catch (err) { console.error(err); } finally { setActionLoading(null); }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="mx-auto max-w-7xl text-center py-12 text-gray-500">Loading referral payout data...</div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="mx-auto max-w-7xl">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Referral Payouts</h1>
          <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Referral Payouts</h1>
            <p className="mt-2 text-sm text-gray-600">
              Review and manage referral payout requests
            </p>
          </div>
          <button
            onClick={() => { setLoading(true); fetchData(); }}
            disabled={loading}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>

        {/* Overview Stats */}
        {stats && (
          <div className="mb-8 grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: 'Total Outstanding', value: formatXrpFromDrops(stats.outstanding_drops) },
              { label: 'Committed', value: formatXrpFromDrops(stats.committed_drops) },
              { label: 'Uncommitted', value: formatXrpFromDrops(stats.uncommitted_drops) },
              { label: 'Pending Requests', value: String(stats.pending_requests) },
              { label: 'Paid Out (All Time)', value: formatXrpFromDrops(stats.total_paid_drops), green: true },
            ].map((card) => (
              <div key={card.label} className="rounded-lg border border-gray-200 bg-white p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{card.label}</p>
                <p className={`mt-1 text-lg font-semibold ${card.green ? 'text-green-600' : 'text-gray-900'}`}>{card.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Reject Modal */}
        {rejectId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Payout</h3>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for rejection..."
                className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm text-gray-900 mb-4 focus:outline-none focus:border-blue-500"
                rows={3}
              />
              <div className="flex justify-end gap-3">
                <button onClick={() => { setRejectId(null); setRejectReason(''); }} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button onClick={handleReject} disabled={!rejectReason.trim() || actionLoading === rejectId} className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
                  {actionLoading === rejectId ? 'Rejecting...' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {detailData && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDetailData(null)}>
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Payout Detail</h3>
                <button onClick={() => setDetailData(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
              </div>

              <div className="space-y-4 text-sm">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Referrer</p>
                  <p className="text-gray-900 font-medium">
                    {detailData.referrer?.username || formatAddr(detailData.referrer?.xrpl_address || detailData.payout?.payout_address)}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Amount</p>
                    <p className="text-gray-900 font-semibold">{formatXrpFromDrops(Number(detailData.payout?.amount_drops))}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Status</p>
                    <StatusBadge status={detailData.payout?.status} />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Payout Address</p>
                    <p className="text-gray-900 font-mono text-xs">{detailData.payout?.payout_address}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Requested</p>
                    <p className="text-gray-900">{detailData.payout?.created_at ? formatDate(detailData.payout.created_at) : '-'}</p>
                  </div>
                </div>

                {detailData.balance && (
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Balance Verification</p>
                    <div className="grid grid-cols-2 gap-2 text-gray-700">
                      <div>Total Accrued: <span className="font-medium text-gray-900">{formatXrpFromDrops(Number(detailData.balance.total_accrued_drops))}</span></div>
                      <div>Total Paid: <span className="font-medium text-gray-900">{formatXrpFromDrops(Number(detailData.balance.total_paid_drops))}</span></div>
                      <div>In-flight: <span className="font-medium text-gray-900">{formatXrpFromDrops(Number(detailData.balance.inflight_drops))}</span></div>
                      <div>Available: <span className="font-medium text-gray-900">{formatXrpFromDrops(Number(detailData.balance.available_balance_drops))}</span></div>
                    </div>
                  </div>
                )}

                {detailData.earnings && detailData.earnings.length > 0 && (
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Earnings by Launch</p>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-1.5 font-medium text-gray-500">Launch</th>
                          <th className="text-right py-1.5 font-medium text-gray-500">Mints</th>
                          <th className="text-right py-1.5 font-medium text-gray-500">Earned</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailData.earnings.map((e: any) => (
                          <tr key={e.launch_id} className="border-b border-gray-100">
                            <td className="py-1.5 text-gray-900">{e.launch_name}</td>
                            <td className="py-1.5 text-right text-gray-700">{e.referred_mints}</td>
                            <td className="py-1.5 text-right text-gray-900 font-medium">{formatXrpFromDrops(Number(e.total_earned_drops))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Pending Payout Requests */}
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">
              Pending Requests ({total})
            </h2>
            {payouts.length > 0 && (
              <button
                onClick={() => {
                  const rows = [['Referrer', 'Address', 'Amount (XRP)', 'Status', 'Payout Address', 'Date']];
                  payouts.forEach((p) => rows.push([p.username || '', p.xrpl_address || '', (Number(p.amount_drops) / 1_000_000).toFixed(6), p.status, p.payout_address, new Date(p.created_at).toISOString()]));
                  const csv = rows.map((r) => r.join(',')).join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url; a.download = 'referral-payouts.csv'; a.click();
                  URL.revokeObjectURL(url);
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Export CSV
              </button>
            )}
          </div>

          {payouts.length === 0 ? (
            <p className="text-sm text-gray-500 py-12 text-center">No pending payout requests.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Referrer</th>
                    <th className="text-right py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Payout Address</th>
                    <th className="text-right py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {payouts.map((p) => (
                    <tr
                      key={p.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => {
                        setDetailLoading(true);
                        getAdminReferralPayoutDetail(p.id).then(setDetailData).catch(() => {}).finally(() => setDetailLoading(false));
                      }}
                    >
                      <td className="py-3 px-6 text-sm font-medium text-gray-900">
                        {p.username || formatAddr(p.xrpl_address || p.payout_address)}
                      </td>
                      <td className="py-3 px-6 text-right text-sm text-gray-900 whitespace-nowrap">
                        {formatXrpFromDrops(Number(p.amount_drops))}
                      </td>
                      <td className="py-3 px-6 text-sm text-gray-500 whitespace-nowrap">
                        {formatDate(p.created_at)}
                      </td>
                      <td className="py-3 px-6">
                        <StatusBadge status={p.status} />
                      </td>
                      <td className="py-3 px-6 text-sm text-gray-500 font-mono">
                        {formatAddr(p.payout_address)}
                      </td>
                      <td className="py-3 px-6 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          {p.status === 'pending' && (
                            <>
                              <button onClick={() => handleApprove(p.id)} disabled={actionLoading === p.id} className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                                {actionLoading === p.id ? '...' : 'Approve'}
                              </button>
                              <button onClick={() => setRejectId(p.id)} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                                Reject
                              </button>
                            </>
                          )}
                          {p.status === 'failed' && (
                            <button onClick={() => handleRetry(p.id)} disabled={actionLoading === p.id} className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                              {actionLoading === p.id ? '...' : 'Retry'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
